import * as Notifications from '@common/Notification';
import { CONFIG } from '@constants/config';
import { IMAGES } from '@constants/images';
import { APP_ROUTES } from '@constants/routes';
import { getOrderItem } from '@helpers/OrderHelpers';

// --- Breadcrumbs

export function getBreadcrumbsFromTrail(trail, options = {}) {
  const { shouldIncludeHome = true, omitLast = false } = options;

  const items = [];

  let path = APP_ROUTES.CATEGORIES;

  if (shouldIncludeHome) {
    items.push({
      href: APP_ROUTES.HOME,
      name: 'NAVIGATION.HOME',
      translate: true
    });
  }

  trail?.forEach((item, index) => {
    const { slug, name } = item;

    if (index === trail.length - 1) {
      items.push({ href: `${APP_ROUTES.PRODUCTS}/${slug}`, name });
    } else {
      path += `/${slug}`;
      items.push({ href: path, name });
    }

  });

  if (omitLast) {
    items.pop();
  }

  return items;
}

// --- Available

export function isProductAvailable(product) {
  const { isAvailable } = product || {};
  return isAvailable;
}

// --- Quantity

export function getCartQuantity(order, product) {
  if (!order || !order?.items?.length > 0) return 0;

  try {
    const item = getOrderItem(order, product);
    return item?.quantity || 0;
  } catch (err) {
    console.error('Error while getting the order quantity for product', product, err);
  }

  return 0;
}

export function getMaxAllowedQuantity(product = {}, errors = {}) {
  const { maxAvailableQuantity = Infinity, maxAllowedQuantity = Infinity } = product;

  const value = Math.min(maxAllowedQuantity, maxAvailableQuantity);

  const errorKey = maxAvailableQuantity < maxAllowedQuantity ? 'CART_ADD_MAX_AVAILABLE_QUANTITY' : 'CART_ADD_MAX_ALLOWED_QUANTITY';
  const errorMessage = errors[errorKey] || DEFAULT_ERROR_MESSAGE;

  return [value, errorMessage];
}

export function checkMaxQuantityReached(oldQuantity, newQuantity, product, errors, payload = {}) {
  const { analytics } = payload;

  const [maxQuantity, errorMessage] = getMaxAllowedQuantity(product, errors);
  const isMaximumReached = newQuantity === maxQuantity;

  if (oldQuantity < newQuantity && isMaximumReached) {
    Notifications.showError(errorMessage, { autoClose: 2000 });

    if (analytics) {
      analytics.viewCartError(errorMessage);
    }
  }

  return isMaximumReached;
}

export function handleChangeQuantityClick(oldQuantity, newQuantity, handleRemove, handleEdit) {
  if (newQuantity !== oldQuantity) {
    if (newQuantity === 0) {
      handleRemove();
    } else {
      handleEdit(oldQuantity, newQuantity);
    }
  }
}

// --- Favorite

export function isFavorite(favorites, product = {}) {
  if (!favorites || !Array.isArray(favorites)) return false;
  const { variantCode } = product;
  return favorites.find((p) => p.code === variantCode);
}

// --- Order Details

export function isOrderDetailsProductUnavailable(product) {
  const { state } = product || {};
  return [
    ORDER_SUMMARY_PRODUCT_STATES.DELETED,
    ORDER_SUMMARY_PRODUCT_STATES.REPLACED,
    ORDER_SUMMARY_PRODUCT_STATES.PARTIALLY_REPLACED,
    ORDER_SUMMARY_PRODUCT_STATES.DECREASED_QUANTITY,
    ORDER_SUMMARY_PRODUCT_STATES.QUANTITY_CHANGED
  ].includes(state);
}

// --- Variants

/**
 * Get product variants
 * [default variant, accelerated sale variant]
 * @param {Object} product
 * @param {Object} params - { shouldShowGeniusPrice }
 * @returns {Array} - list of variants
 */
export function getProductVariants(product, params = {
  shouldShowGeniusPrice: false,
}) {
  if (!product) return [];
  return [
    getProductDefaultVariant(product, params),
    getProductAcceleratedSaleVariant(product, params),
  ];
}

/**
 * Get default product variant
 * @param {Object} product
 * @param {Object} params - { shouldShowGeniusPrice }
 * @returns {Object} - default product variant
 */
export function getProductDefaultVariant(product, params = {
  shouldShowGeniusPrice: false,
}) {
  const {
    slug, currency
  } = product || {};

  return getVariant(
    product,
    {
      slug, currency, isDefault: true,
    },
    params
  );
}

/**
 * Get accelerated sale product variant
 * @param {Object} product
 * @param {Object} params - { shouldShowGeniusPrice }
 * @returns {Object} - accelerated sale product variant
 */
export function getProductAcceleratedSaleVariant(product, params = {
  shouldShowGeniusPrice: false,
}) {
  const {
    slug, currency, images, saveMe
  } = product || {};

  return getVariant(
    saveMe,
    {
      slug, currency, images, isAcceleratedSale: true,
    },
    params
  );
}

/**
 * Get variant
 * @param {Object} variant
 * @param {Object} extraProps - variant specific props
 * @param {Object} params - { shouldShowGeniusPrice }
 * @returns {Object} - variant
 */
function getVariant(variant, extraProps, params) {
  const {
    variantCode,

    // flags
    isAvailable,

    // quantity
    maxAvailableQuantity,
    maxAllowedQuantity,

    // images, labels / banner
    image = [],
    images = [],
    labels = [],
    banner = {},
  } = variant || {};

  const pricingProps = getProductPriceProps(variant, { ...params });

  return {
    variantCode,

    // flags
    isAvailable,

    // pricing
    ...pricingProps,

    // quantity
    maxAvailableQuantity,
    maxAllowedQuantity,

    // images
    image,
    images,

    // labels / banner
    labels,
    banner,

    // extraProps
    ...extraProps,
  };
}

/**
 * Get product variant pricing props
 * @param {Object} product
 * @param {Object} params - { shouldShowGeniusPrice }
 * @returns {Object} - pricing props object
 */
export function getProductPriceProps(product = {}, params = {
  shouldShowGeniusPrice: false,
}) {

  // Product

  const {
    //
    originalPrice,
    price,
    unitPriceLabel,
    //
    discountLabel,
    //
    priceLabel,
    priceLabelColor,
    //
    priceColor,
    priceIcon,
    priceTooltip,
    //
    geniusPrice,
    geniusUnitPricePerLabel,
    geniusDiscountLabel,
    //
    promotionalPrice,
    //
  } = product || {};

  // Params

  const {
    shouldShowGeniusPrice,
  } = params || {};

  // Genius Props

  if (shouldShowGeniusPrice && !!geniusPrice) {
    return {
      hasGeniusPrice: true,
      //
      originalPriceStrikethrough: true,
      originalPrice: originalPrice || price,
      price: geniusPrice,
      //
      unitPriceLabel: geniusUnitPricePerLabel || unitPriceLabel,
      discountLabel: geniusDiscountLabel,
      //
      priceColor: DISCOUNT_PRICE_COLOR,
      priceIcon: IMAGES.BRANDING.GENIUS_ICON,
      priceTooltip,
    };
  }

  if (promotionalPrice) {
    return {
      hasGeniusPrice: false,
      //
      originalPriceStrikethrough: false,
      originalPrice: price,
      price: promotionalPrice,
      //
      unitPriceLabel,
      discountLabel,
      //
      priceColor: INCREDIBLE_DISCOUNTS_PRICE_COLOR,
      priceIcon: IMAGES.PROMOTIONS.INCREDIBLE_DISCOUNTS_PRICE_ICON,
      priceTooltip,
    };
  }

  // Default Props

  return {
    hasGeniusPrice: false,
    //
    originalPriceStrikethrough: true,
    originalPrice,
    price,
    //
    unitPriceLabel,
    discountLabel,
    //
    priceColor: discountLabel ? DISCOUNT_PRICE_COLOR : priceColor,
    priceIcon,
    priceTooltip,
    //
    priceLabel,
    priceLabelColor,
  };
}

/**
 * Enhance order item
 * adds pricing props - priceColor, priceIcon
 * @param {Object} orderItem
 * @param {Object} params - { shouldShowGeniusPrice }
 * @returns {Object} - enhanced order item
 */
export function enhanceOrderItem(orderItem, params = {
  shouldShowGeniusPrice: false,
}) {

  // Order Item

  const {
    hasGeniusDiscount,
    subtotal: price,
    originalSubtotal: originalPrice,
  } = orderItem || {};

  // Params

  const {
    shouldShowGeniusPrice,
  } = params || {};

  const hasDiscount = (originalPrice && price !== originalPrice);

  // Genius Discount - Price Color / Icon

  if (shouldShowGeniusPrice && hasGeniusDiscount) {
    orderItem.priceColor = DISCOUNT_PRICE_COLOR;
    orderItem.priceIcon = IMAGES.BRANDING.GENIUS_ICON;
    return orderItem;
  }

  // Generic Discount - Price Color

  if (hasDiscount) {
    orderItem.priceColor = DISCOUNT_PRICE_COLOR;
    return orderItem;
  }

  // Default

  return orderItem;
}

// --- Link

export function getProductLink(slug, isAcceleratedSale) {
  if (!slug) return null;

  return isAcceleratedSale
    ? `${APP_ROUTES.PRODUCTS}/${slug}?sm=true`
    : `${APP_ROUTES.PRODUCTS}/${slug}`;
}

// --- Same brand link

export function getSameBrandLink(brand, brandFilterCode) {
  return `${APP_ROUTES.SEARCH}/${brand}/filters/brand+${brandFilterCode}`;
}

// --- Product delete notification

const FAVORITES_TOAST_ID = 'productFavoritesAction';
/**
 *  Shows a toast notification
 * @param {object} payload.product.image - the added/removed product image
 * @param {object} payload.product.name - the added/removed product name
 * @param {object} payload.component - the component used as the toast body
 * @param {object} payload.message - the message to be shown in the notification
 */
export function showFavoritesNotification(payload) {

  const {
    product, message,
    component: Component // workaround to avoid dependency cycle
  } = payload;

  const toastBody = (
    <Component product={product} message={message} />
  );

  if (Notifications.isActive(FAVORITES_TOAST_ID)) {
    Notifications.update(FAVORITES_TOAST_ID, {
      autoClose: CONFIG.DURATIONS.PRODUCT_FAVORITES_TOAST_AUTOCLOSE,
      containerId: Notifications.TOAST_CONTAINER_IDS.CUSTOM,
      toastId: FAVORITES_TOAST_ID,
      render: toastBody,
    });
  } else {
    Notifications.showProgress(
      toastBody,
      {
        autoClose: CONFIG.DURATIONS.PRODUCT_FAVORITES_TOAST_AUTOCLOSE,
        containerId: Notifications.TOAST_CONTAINER_IDS.CUSTOM,
        toastId: FAVORITES_TOAST_ID
      },
    );
  }
}
// --- Product delete notification

const REMOVE_PRODUCT_SUCCESS_TOAST_ID = 'productRemoveSuccess';
/**
 *  Shows a toast notification
 * @param {object} payload
 * @param {object} payload.component - the component used as the toast body
 * @param {object} payload.order - the active order
 * @param {object} payload.product - the deleted product
 * @param {object} payload.listProps - list props used for analytics
 * @param {object} payload.onSuccess - passed to the toast body component
 */
export function showDeleteNotification(payload) {

  const {
    order, product, listProps, onSuccess,
    component: Component // workaround to avoid dependency cycle
  } = payload;

  const toastBody = (
    <Component
      order={order}
      orderItem={product}
      listProps={listProps}
      onSuccess={onSuccess}
    />
  );

  if (Notifications.isActive(REMOVE_PRODUCT_SUCCESS_TOAST_ID)) {
    Notifications.update(REMOVE_PRODUCT_SUCCESS_TOAST_ID, {
      autoClose: CONFIG.DURATIONS.PRODUCT_DELETE_TOAST_AUTOCLOSE,
      toastId: REMOVE_PRODUCT_SUCCESS_TOAST_ID,
      render: toastBody,
    });
  } else {
    Notifications.showProgress(
      toastBody,
      {
        autoClose: CONFIG.DURATIONS.PRODUCT_DELETE_TOAST_AUTOCLOSE,
        toastId: REMOVE_PRODUCT_SUCCESS_TOAST_ID,
      }
    );
  }
}

/* Images */

export function getThumbnailSrc(image) {
  return image?.thumbnail?.default || image?.large?.default;
}

// Gifts

export function getOrderGiftProducts(gifts, imageKey = 'thumbnail') {
  return gifts?.map((gift) => {
    const { product } = gift;
    return {
      ...product,
      image: product?.image ? product.image[imageKey] : null
    };
  });
}

// --- Constants

// TODO: better fallback mechanism
const DEFAULT_ERROR_MESSAGE = 'Ai atins cantitatea maxima';

const DISCOUNT_PRICE_COLOR = '#FF7070';

const INCREDIBLE_DISCOUNTS_PRICE_COLOR = '#399F26';

export const ORDER_SUMMARY_PRODUCT_STATES = {
  DELETED: 'deleted',
  REPLACED: 'replaced',
  PARTIALLY_REPLACED: 'partial_replaced',
  DECREASED_QUANTITY: 'decreased',
  QUANTITY_CHANGED: 'quantity_changed'
};
